import {
  COMBINE_GRAPHS_MODE, DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZES, DataTable,
  DataTableRowFragment, DateFromNow, FetchDataError, LayoutContext, Pagination, Value,
  DataFilteringLayout, parseTimestamp,
} from 'apstra-ui-common';
import cx from 'classnames';
import {
  compact as lodashCompact, find, findIndex, forEach, get, has, head, isEmpty, isFunction,
  isNumber, keys, map, times, transform
} from 'lodash';
import {observer} from 'mobx-react';
import {FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Message, Grid, Table, Icon, Placeholder} from 'semantic-ui-react';

import IBAContext from '../IBAContext';
import {ProbePattern} from '../checkForPatterns';
import {STAGE_DATA_SOURCE} from '../consts';
import {regexFilterRenderer, advancedStringFilterRenderer, rangeFilterRenderer} from '../filterRenderers';
import {anomalyColors, discreteStateColors} from '../../graphColors';
import {stagePropertiesRenderers} from '../stagePropertiesRenderers';
import {
  processorHasRaiseAnomaliesFlag, processorCanRaiseAnomalies, getStageDataSchema, getInputStages,
  getProcessorByStageName, getTelemetryServiceSchema,
} from '../stageUtils';
import {Probe, ProbeProcessor, ProbeStage, RenderingStrategy} from '../types';
import {UseSearchStringFiltersResult} from '../../useSearchStringFilters';

import AnomalyValues from './AnomalyValues';
import {ColumnDescription} from './ColumnDescription';
import {useScrollContext} from './ProbeDetails/ScrollContext';
import {SpotlightKeysTableRow} from './SpotlightKeysTableRow';
import {StageDataOptions} from './StageDataOptions';
import StageSearchBox, {getStageSearchSchema} from './StageSearchBox';
import {TelemetryServiceStatus} from './TelemetryServiceStatus';
import {PrimitiveValue} from './valueRenderers';

import './StageData.less';

type StageDataProps = {
  activePage: number;
  compact?: boolean;
  fetchDataError: any;
  filters: any;
  loaderContent?: ReactNode;
  loaderVisible: boolean;
  pageSize: number;
  patternData?: any; // passed to StageDataRenderer
  patternDescription: ProbePattern | null;
  probe: Probe;
  processor: ProbeProcessor;
  renderingStrategy: RenderingStrategy;
  sorting: any;
  stage: ProbeStage;
  stageItems: any[];
  stageLink: ReactNode;
  totalCount?: number | null;
  updateFilters: UseSearchStringFiltersResult<any>['updateFilters'];
  updatePagination: UseSearchStringFiltersResult<any>['updatePagination'];
  updateSorting: UseSearchStringFiltersResult<any>['updateSorting'];
  visibleColumns?: string[];
};

export const StageData: FC<StageDataProps> = observer((props) => {
  const {
    activePage,
    compact,
    fetchDataError,
    filters,
    loaderContent,
    loaderVisible,
    pageSize,
    patternDescription,
    probe,
    processor,
    renderingStrategy,
    sorting,
    stage,
    stageItems,
    stageLink,
    totalCount,
    updateFilters: updateFiltersFromProps,
    updatePagination,
    updateSorting,
    visibleColumns,
  } = props;
  const {blueprintId, isFreeform, processorDefinitions} = useContext(IBAContext);
  const {mainContentElement} = useContext(LayoutContext) as any;
  const scrollContext = useScrollContext();

  const [previousScrollPositionTop, setPreviousScrollPositionTop] = useState<number | null>(null);
  const spotlightViewRef = useRef<HTMLDivElement>(null);
  const rendererPropsRef = useRef<any>();

  const [highlightedColumn, setHighlightedColumn] = useState(null);

  const highlightPropertyColumn = useCallback((column) => {
    if (column !== highlightedColumn) setHighlightedColumn(column);
  }, [highlightedColumn]);

  const prevLoaderVisibleRef = useRef(false);

  const showContextInfo = filters.showContextInfo || !!patternDescription?.renderingStrategy?.alwaysUseContext;
  const maySwitchSpotlightMode = !compact;

  useEffect(() => {
    if (prevLoaderVisibleRef.current && !loaderVisible) {
      if (!filters.spotlightMode && previousScrollPositionTop) {
        mainContentElement?.scrollTo({top: previousScrollPositionTop, behavior: 'smooth'});
        setPreviousScrollPositionTop(null);
      }
      if (filters.spotlightMode && spotlightViewRef.current) {
        spotlightViewRef.current.focus();
      }
    }
    prevLoaderVisibleRef.current = loaderVisible;
  }, [filters.spotlightMode, loaderVisible, mainContentElement, previousScrollPositionTop]);

  const updateFilters = useCallback((newFilters, opts?: any) => {
    const processedFilters = {...newFilters};
    if (patternDescription?.renderingStrategy?.clearFilterOnContextTrigger &&
      has(newFilters, 'showContextInfo') &&
      filters?.showContextInfo !== newFilters?.showContextInfo
    ) {
      processedFilters.filter = {};
    }
    updateFiltersFromProps(processedFilters, opts);
  }, [
    filters?.showContextInfo, patternDescription?.renderingStrategy?.clearFilterOnContextTrigger, updateFiltersFromProps
  ]);

  const switchToListMode = useCallback(() => {
    updateFilters({...filters, spotlightMode: false});
    const page = Math.floor(activePage / DEFAULT_PAGE_SIZE) + 1;
    updatePagination({activePage: page, pageSize: DEFAULT_PAGE_SIZE});
    scrollContext?.scrollToElement('stage details', stage.name);
  }, [activePage, filters, scrollContext, stage.name, updateFilters, updatePagination]);

  const anomaliesByItems = useMemo(() => {
    if (!processorCanRaiseAnomalies(processor)) {
      return {};
    }

    return transform(stageItems, (result, item) => {
      const {
        value,
        actual_value: actualValue, anomalous_value: anomalyValue,
        anomalous_value_min: valueMin, anomalous_value_max: valueMax
      } = item;
      if (value !== 'true' || get(item, ['properties', 'raise_anomaly']) === false) return;
      const anomaly = {actual: {value: actualValue}, anomalous: {}} as any;
      if (anomalyValue) anomaly.anomalous.value = anomalyValue;
      if (isNumber(valueMin)) anomaly.anomalous.value_min = valueMin;
      if (isNumber(valueMax)) anomaly.anomalous.value_max = valueMax;
      result[item.id] = anomaly;
    }, {});
  }, [processor, stageItems]);

  const onSpotlightModeKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      switchToListMode();
    }
  }, [switchToListMode]);

  const valueColumnName = filters.valueColumnName || head(keys(stage.values));

  const stageGraphColors = useMemo(() => {
    let currentProcessor: ProbeProcessor | null = processor;

    let processorWithRaiseAnomalyFlag: any = null;
    const stages = [stage];
    do {
      processorWithRaiseAnomalyFlag = processorHasRaiseAnomaliesFlag(currentProcessor!) ? currentProcessor : null;
      const inputStage = stages.pop();
      currentProcessor = getProcessorByStageName({probe, stageName: inputStage!.name});
      stages.push(...getInputStages({probe, processor: currentProcessor!}));
    } while (!isEmpty(stages) && !processorWithRaiseAnomalyFlag);

    return processorWithRaiseAnomalyFlag && processorCanRaiseAnomalies(processorWithRaiseAnomalyFlag) ?
      anomalyColors : discreteStateColors;
  }, [probe, processor, stage]);

  const renderProperty = ({name, value, item}) => (
    <Value
      name={name}
      value={value}
      item={item}
      renderers={stagePropertiesRenderers}
    />
  );

  const tableSchema = useMemo(() => {
    const isTelemetryServiceWarningsDataSource = filters.dataSource === STAGE_DATA_SOURCE.telemetry_service_warnings;
    const result = isTelemetryServiceWarningsDataSource ?
      getTelemetryServiceSchema(stageItems) :
      getStageDataSchema({
        probe,
        stage,
        processor,
        renderingStrategy,
        processorDefinitions,
        showValue: !filters.spotlightMode && !isTelemetryServiceWarningsDataSource,
        valueColumnName: filters.dataSource === STAGE_DATA_SOURCE.time_series ? valueColumnName : null,
      });

    forEach(result, (column) => {
      column.valueSchema ??= stage.values[column.name] ?? null;
      if (column.name === 'anomaly') {
        column.value = ({item}) => item.id in anomaliesByItems;
        column.formatter = ({item}) => <AnomalyValues anomaly={anomaliesByItems[item.id]} />;
      } else if (column.name === 'warning') {
        column.formatter = ({value}) => <TelemetryServiceStatus warning={value} />;
      } else if (column.valueSchema) {
        const Renderer = renderingStrategy.getRenderer?.(column.valueSchema) ??
          renderingStrategy.ValueRenderer ??
          PrimitiveValue;
        column.formatter = (args) => <Renderer {...rendererPropsRef.current} {...args} />;
      } else if (column.name === 'timestamp') {
        column.value = ({item}) => parseTimestamp(item.timestamp);
        column.formatter = ({value}) => <DateFromNow date={value} />;
      } else if (!column.formatter) {
        column.formatter = renderProperty;
      }
      // FIXME(vkramskikh): only used makePrecedingStageColumnDefinition to override item and name.
      // Formatters need to be reimplemented as function components accepting actual values.
      if (column.overrideFormatter) {
        column.formatter = column.overrideFormatter(column.formatter);
      }
      if (!column.description && column.type) {
        column.description = <ColumnDescription
          column={column}
          stage={stage}
          stageGraphColors={column.stageGraphColors ?? stageGraphColors}
          patternDescription={showContextInfo && patternDescription}
        />;
      }
    });

    if (isEmpty(visibleColumns)) {
      return result;
    }
    return lodashCompact(map(visibleColumns, (name) => find(result, {name})));
  }, [
    anomaliesByItems, filters.dataSource, filters.spotlightMode, patternDescription, processor, processorDefinitions,
    renderingStrategy, showContextInfo, stage, stageGraphColors, stageItems, valueColumnName, visibleColumns, probe
  ]);

  const valueSchemas = useMemo(
    () => transform(
      tableSchema,
      (result, column) => {
        if (column.valueSchema) result[column.name] = column.valueSchema;
      },
      {...stage.values}
    ),
    [stage.values, tableSchema]
  );

  rendererPropsRef.current = useMemo(() => ({
    stageDataProps: props,
    valueSchemas,
    valueColumnName,
    defaultColors: stageGraphColors,
    patternDescription,
  }),
  [patternDescription, props, stageGraphColors, valueColumnName, valueSchemas]);

  const mayCombineGraphs = useMemo(() => {
    if (filters.spotlightMode) return false;
    const valueSchema = stage.values[valueColumnName];
    return isFunction(renderingStrategy.graphCombiningAvailable) ?
      renderingStrategy.graphCombiningAvailable(valueSchema) :
      renderingStrategy.graphCombiningAvailable ?? false;
  }, [filters.spotlightMode, renderingStrategy, stage.values, valueColumnName]);

  const getDataTableCellProps = ({name, item}) => {
    const valueCell = name in valueSchemas;
    const expandableValueCell = name in stage.values && !renderingStrategy.noSpotlightMode;
    const switchToSpotlightMode = (itemId) => {
      setPreviousScrollPositionTop(mainContentElement?.scrollTop !== undefined ?
        mainContentElement?.scrollTop
        :
        (document.documentElement || document.body.parentNode || document.body).scrollTop);
      updateFilters({...filters, spotlightMode: true});
      const page = (activePage - 1) * pageSize + findIndex(stageItems, (item: any) => item.id === itemId) + 1;
      updatePagination({activePage: page, pageSize: 1});
      scrollContext?.scrollToElement('stage details', stage.name);
    };

    return ({
      onClick: expandableValueCell && maySwitchSpotlightMode ? () => switchToSpotlightMode(item.id) : null,
      className: cx({
        'value-cell': valueCell,
        expandable: expandableValueCell,
        highlight: highlightedColumn === name,
      })
    });
  };

  const ChosenValueColumnRenderer = useMemo(() => {
    const valueSchema = valueSchemas[valueColumnName];

    return renderingStrategy.getRenderer?.(valueSchema) ??
      renderingStrategy.ValueRenderer ??
      PrimitiveValue;
  }, [renderingStrategy, valueColumnName, valueSchemas]);

  const renderStageDataTableFooterContent = (wrap, colSpan?: number) => {
    if (!stageLink) return null;
    const footer = (
      <Table.Footer>
        <Table.Row>
          <Table.HeaderCell
            className={cx('stage-footer', {standalone: wrap})}
            colSpan={colSpan || tableSchema.length}
            textAlign='center'
          >
            {stageLink}
          </Table.HeaderCell>
        </Table.Row>
      </Table.Footer>
    );
    return wrap ? <Table size='small'>{footer}</Table> : footer;
  };

  const wrapWithLoader = (renderContent, noDataContent: any = undefined, alwaysShownContent: any = null) => {
    return loaderVisible ?
      loaderContent ?? (
        <Placeholder className='stage-placeholder-background' fluid>
          {times(4, (index) =>
            <Placeholder.Paragraph key={index}>
              {times(10, (index) =>
                <Placeholder.Line key={index} />
              )}
            </Placeholder.Paragraph>
          )}
        </Placeholder>
      )
      :
      <>
        {alwaysShownContent}
        {noDataContent || renderContent()}
        {fetchDataError && <FetchDataError error={fetchDataError} />}
      </>;
  };

  const renderSpotlightMode = (noDataContent) => {
    const paginationProps = {
      activePage,
      pageSize,
      pageSizes: [1],
      totalCount,
      onChange: updatePagination,
    };

    const tableParams = {
      labels: transform(tableSchema, (result, schemaItem) => {
        result[schemaItem.name] = schemaItem.label;
      }, {}),
      blueprintId, probe, stage
    };

    return wrapWithLoader(
      () => (
        <div className='item-set-container'>
          <div // eslint-disable-line jsx-a11y/no-static-element-interactions
            ref={spotlightViewRef}
            tabIndex={-1}
            onKeyDown={onSpotlightModeKeyDown}
            className='spotlight-mode-container'
          >
            <div className='spotlight-mode-item item-set'>
              <div className='spotlight-mode-item-value'>
                <ChosenValueColumnRenderer
                  item={stageItems[0]}
                  name={valueColumnName}
                  value={stageItems[0][valueColumnName]}
                  index={0}
                  items={stageItems}
                  expanded
                  {...rendererPropsRef.current}
                />
                {maySwitchSpotlightMode &&
                  <Icon link name='close' onClick={switchToListMode} />
                }
              </div>
              <Table celled size='small' className='schema-table'>
                <Table.Body>
                  <DataTableRowFragment
                    schema={tableSchema}
                    item={stageItems[0]}
                    params={tableParams}
                    getCellProps={({name, params}) => ({
                      label: params.labels[name]
                    })}
                    CellComponent={SpotlightKeysTableRow}
                  />
                </Table.Body>
                {stageLink &&
                  renderStageDataTableFooterContent(false, 2)
                }
              </Table>
            </div>
            <div className='pagination-centered'>
              <Pagination
                sticky={false}
                separateButtons
                {...paginationProps}
              />
            </div>
          </div>
        </div>
      ),
      noDataContent
    );
  };

  const searchSchema = useMemo(
    () => getStageSearchSchema({
      processorDefinitions, probe, processor, stage, stageItems, dataSource: filters.dataSource, isFreeform
    }),
    [filters.dataSource, isFreeform, probe, processor, processorDefinitions, stage, stageItems]
  );

  const stageDataOptions = !compact && (
    <StageDataOptions
      filters={filters}
      isSpotlightMode={filters.spotlightMode}
      patternDescription={patternDescription}
      probe={probe}
      processor={processor}
      renderingStrategy={renderingStrategy}
      stage={stage}
      updateFilters={updateFilters}
      valueColumnName={valueColumnName}
    />
  );

  const renderFilteredData = (noDataContent) => {
    const {spotlightMode} = filters;
    const pageSizes = spotlightMode ? [1] : DEFAULT_PAGE_SIZES;
    const {
      noAnomalyHighlighting, noPagination = null,
      SearchComponent = StageSearchBox
    } = renderingStrategy;
    const tableParams = {highlightedColumn, blueprintId, probe, stage};
    const paginationProps = {
      activePage,
      pageSize,
      pageSizes,
      totalCount,
      onChange: updatePagination,
    };

    return (
      <DataFilteringLayout
        loaderVisible={loaderVisible}
        fetchDataError={fetchDataError}
        hideActionsAndPagination={compact || spotlightMode}
        paginationProps={(noPagination || spotlightMode) ? undefined : paginationProps}
        SearchComponent={SearchComponent}
        searchProps={{
          filters: filters.filter,
          schema: searchSchema,
          renderers: [regexFilterRenderer, advancedStringFilterRenderer, rangeFilterRenderer],
          onChange: (filter) => updateFilters({...filters, filter}),
          highlightPropertyColumn,
          'aria-label': 'Stage filter query',
          asAccordion: false
        }}
        extraContent={{
          headerRow: stageDataOptions
        }}
      >
        {
          spotlightMode ? renderSpotlightMode(noDataContent) : (
          noDataContent || (
            mayCombineGraphs && filters.combineGraphs !== COMBINE_GRAPHS_MODE.NONE ?
              <div className='combined-graphs'>
                <ChosenValueColumnRenderer
                  item={stageItems[0]}
                  index={0}
                  items={stageItems}
                  expanded
                  {...rendererPropsRef.current}
                />
                {renderStageDataTableFooterContent(true)}
              </div>
            :
              <DataTable
                className='item-set'
                size='small'
                items={stageItems}
                schema={tableSchema}
                sortable={!compact}
                sorting={sorting}
                updateSorting={updateSorting}
                getHeaderCellProps={({name}) => ({
                  collapsing: !(name in stage.values),
                  className: cx({highlight: name === highlightedColumn})
                })}
                getCellProps={getDataTableCellProps}
                getRowProps={({item}) => ({
                  warning: !isEmpty(item.warning),
                  error: !noAnomalyHighlighting && item.id in anomaliesByItems,
                })}
                getItemKey={({id}) => id}
                footer={
                  renderStageDataTableFooterContent(false)
                }
                params={tableParams}
              />
          )
          )
        }
      </DataFilteringLayout>
    );
  };

  const {StageDataRenderer, noDataMessage, SearchComponent = StageSearchBox} = renderingStrategy;

  const noDataContent = isEmpty(stageItems) ?
    <div className='no-data'>
      {
        filters.anomalousOnly ?
          <Message success icon='check circle' header='No anomalies!' />
        :
          <Message info icon='info circle' content={noDataMessage || 'No data'} />
      }
      {renderStageDataTableFooterContent(true)}
    </div> :
    null;

  const stageControls = (
    <Grid stackable className='data-filtering-layout'>
      {stageDataOptions}
      <Grid.Row>
        <Grid.Column width={16}>
          <SearchComponent
            filters={filters.filter}
            stage={stage}
            stageItems={stageItems}
            processor={processor}
            dataSource={filters.dataSource}
            onChange={(filter) => updateFilters({...filters, filter})}
            highlightPropertyColumn={highlightPropertyColumn}
            aria-label='Stage filter query'
          />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );

  return StageDataRenderer ?
    wrapWithLoader(
      () => (
        <>
          <StageDataRenderer {...props} />
          {renderStageDataTableFooterContent(true)}
        </>
      ),
      noDataContent,
      stageControls
    ) :
    wrapWithLoader(
      () => (
        <div className='item-set-container'>
          {renderFilteredData(noDataContent)}
        </div>
      )
    );
});
